import React, { useState } from 'react';

import { AlertMessage } from '../../../components/AlertMessage/component';
import { CheckboxSelector } from '../../../components/Forms/CheckboxSelector/CheckboxSelector';
import { Button } from '../../../components/Buttons/Button/Button';
import {
  Container,
  Heading,
  SubHeading,
  Table,
  UserRow,
  UserIcon,
  VSpace,
} from './styles';
import { UserSummary } from '../../../../ReduxStorage/Users/constants';
import type {
  UserSubscriptionRoleType,
  UserSubscriptionSummary,
} from '../../../../ReduxStorage/UserSubscriptionPlans/selectors';
import {
  SUBSCRIPTION_ROLE_ADMIN,
  SUBSCRIPTION_ROLE_OWNER,
  UserSubscriptionPlanSummary,
} from '../../../../ReduxStorage/UserSubscriptionPlans/constants';
import { Link } from '../../Link';
import { ROUTE_SSO } from '../../../../ReduxStorage/routeActions';
import { HELP_ORIGIN } from '../../../../constants';

const SUBSCRIPTION_ADMIN_PRIVILEDGES_BANNER_MESSAGE =
  'They can view account usage, billing details, and add or remove other subscription admins. However, they will not be able to change the subscription plan. Learn more ';

export type ManageUsersProps = {
  userSubscriptionPlan: UserSubscriptionPlanSummary;
  subscriptionId: string;
  linkedCoursePaths: string[];
  existingOwnersAndAdmins: UserSubscriptionSummary[];
  userSuggestions: UserSummary[];
  linkedInstitutionPaths: string[];
  currentUserIsStaff: boolean;
  getUserSuggestion: (params: unknown) => Promise<void>;
  fetchOwnerAndAdmins: () => Promise<void>;
  inviteUserToSubscription: (
    userdId: string,
    subscriptionId: string
  ) => Promise<void>;
  revokeUserAccessToSubscription: (subscriptionRoleId: string) => Promise<void>;
};

const getRoleLabel = (roleType: UserSubscriptionRoleType): string => {
  if (roleType === SUBSCRIPTION_ROLE_OWNER) {
    return 'Subscription owner';
  } else if (roleType === SUBSCRIPTION_ROLE_ADMIN) {
    return 'Subscription admin';
  }

  return 'N/A';
};

export const ManageUsers = React.memo(
  ({
    userSubscriptionPlan,
    subscriptionId,
    linkedCoursePaths,
    existingOwnersAndAdmins,
    userSuggestions,
    linkedInstitutionPaths,
    currentUserIsStaff,
    getUserSuggestion,
    fetchOwnerAndAdmins,
    inviteUserToSubscription,
    revokeUserAccessToSubscription,
  }: ManageUsersProps) => {
    const [selectedOptions, setSelectedOptions] = useState({});
    // const [emailAddress, setEmailAddress] = useState('');
    const [ownerAndAdminsLoaded, setOwnerAndAdminsLoaded] = useState(false);

    if (!ownerAndAdminsLoaded) {
      fetchOwnerAndAdmins();
      setOwnerAndAdminsLoaded(true);
    }

    // Currying the param here so that we can
    // 1. Give a valid function type to the onClick handler
    // 2. Satisfy TS due to sideaffect of creating a new scope with inline callback functions
    //    in the onClick field which disregards the null check above
    // NB: If there is a simpler way, please comment / change.
    const onRemoveAccess = (subscriptionRoleId: string) => () =>
      revokeUserAccessToSubscription(subscriptionRoleId);

    return (
      <Container>
        <Heading>Manage Users</Heading>

        <SubHeading>Existing subscription owners and admins</SubHeading>
        <Table>
          <tbody>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Action</th>
            </tr>
            {existingOwnersAndAdmins.map((item) => {
              if (!currentUserIsStaff && item.isStaff) {
                return null;
              }

              return (
                <tr>
                  <td>
                    <UserRow>
                      <UserIcon src={item.smallAvatar} />
                      <a href="?todo=replace-this">{item.fullName}</a>
                    </UserRow>
                  </td>
                  <td>{getRoleLabel(item.roleType)}</td>
                  <td className="action-column">
                    {item.roleType === SUBSCRIPTION_ROLE_ADMIN &&
                    item.subscriptionRoleId !== null ? (
                      <Button
                        label="Remove access"
                        colour="kelp"
                        onClick={onRemoveAccess(item.subscriptionRoleId)}
                      />
                    ) : (
                      'Cannot remove'
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {/* <Paginate
          page={1}
          totalPages={20}
          pageSize={2}
          totalResults={100}
          handlePageChange={noop}
          showPageSizeSelector
          handlePageSizeChange={noop}
        /> */}
        <VSpace />

        <SubHeading>Inviting new subscription admin</SubHeading>
        <AlertMessage
          kind="info"
          title="Subscription admin privileges"
          message={
            <div>
              {SUBSCRIPTION_ADMIN_PRIVILEDGES_BANNER_MESSAGE}
              <Link
                to={{
                  type: ROUTE_SSO,
                  params: {
                    action: 'help',
                  },
                  query: {
                    url: `${HELP_ORIGIN}/t/y4htx4t/how-do-i-add-someone-as-a-subscription-admin`,
                  },
                }}
                target="_blank"
              >
                here
              </Link>
              .
            </div>
          }
        />
        <VSpace />

        <SubHeading>Invite existing users</SubHeading>
        <CheckboxSelector
          options={userSuggestions}
          optionKey="userId"
          getLabel={(item) => `${item.fullName} (${item.profileName})`}
          placeholder="Search for existing users here"
          selectorPrompt={
            <div>Search for an existing user on OpenLearning</div>
          }
          OptionLabel={({ __selectorLabel, smallAvatar: icon }) => (
            <UserRow>
              <UserIcon src={icon} />
              <div>{__selectorLabel}</div>
            </UserRow>
          )}
          OptionLabelSeparatorInDropdown={<div>&nbsp;&nbsp;</div>}
          {...{ selectedOptions, setSelectedOptions }}
          onInputValueChange={(value: string) => {
            const params: {
              query: string;
              institutionPaths?: string[];
              coursePaths?: string[];
            } = {
              query: value,
            };

            if (userSubscriptionPlan.type === 'portal') {
              params.institutionPaths = linkedInstitutionPaths;
            }

            if (userSubscriptionPlan.type === 'personal') {
              params.coursePaths = linkedCoursePaths;
            }

            getUserSuggestion(params);
          }}
        />
        <VSpace />
        <Button
          label="Send invite"
          icon="envelope"
          colour="kelp"
          onClick={() => {
            Object.keys(selectedOptions).forEach((userId) => {
              inviteUserToSubscription(userId, subscriptionId);
            });
          }}
        />

        {/* <SubHeading>Invite new user via email</SubHeading>
        <div>
          <StyledTextField
            value={emailAddress}
            onValueChange={setEmailAddress}
            placeholder="Email address"
          />
        </div>
        <VSpace />
        <Button
          label="Send invite"
          icon="envelope"
          colour="kelp"
          onClick={noop}
        /> */}
      </Container>
    );
  }
);
